// services/api.js
const API_BASE_URL = 'https://goleiroraizbackend-production.up.railway.app/api/v1';  // 'http://localhost:3001/api/v1'; node env

class ApiService {
  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.accessToken && { Authorization: `Bearer ${this.accessToken}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      let response = await fetch(url, config);
      let data = await response.json();

      // Se recebeu 401 e não for a rota de refresh, tenta renovar token
      if (response.status === 401 && !endpoint.includes('/auth/refresh') && !endpoint.includes('/auth/login')) {
        if (!this.refreshToken) {
          this.handleTokenExpired();
          throw { status: 401, error: { message: 'Sessão expirada' } };
        }

        // Aguarda se outro refresh está em andamento
        if (this.isRefreshing) {
          await this.refreshPromise;
          // Tenta novamente com o novo token
          config.headers.Authorization = `Bearer ${this.accessToken}`;
          response = await fetch(url, config);
          data = await response.json();
        } else {
          // Inicia o refresh
          try {
            await this.tryRefreshToken();
            config.headers.Authorization = `Bearer ${this.accessToken}`;
            response = await fetch(url, config);
            data = await response.json();
          } catch (refreshError) {
            this.handleTokenExpired();
            throw refreshError;
          }
        }
      }

      if (!response.ok) {
        throw {
          status: response.status,
          error: data.error || { message: 'Erro na requisição' }
        };
      }

      return data;
    } catch (error) {
      console.error(`Erro na API ${endpoint}:`, error);
      throw error;
    }
  }

  async tryRefreshToken() {
    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });

        const data = await response.json();

        if (!response.ok || !data.data.accessToken) {
          throw new Error('Falha ao renovar token');
        }

        this.setAccessToken(data.data.accessToken);
        if (data.data.refreshToken) {
          this.setRefreshToken(data.data.refreshToken);
        }
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  handleTokenExpired() {
    this.clearTokens();
    // Avisa o App para voltar à tela de login (sem recarregar a página)
    window.dispatchEvent(new Event('authTokenExpired'));
  }

  /**
   * fetch cru (blob, download, FormData) com o mesmo tratamento de refresh
   * que o request() faz. Retorna a Response sem parsear o corpo.
   */
  async authorizedFetch(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const buildConfig = () => ({
      ...options,
      headers: {
        ...options.headers,
        ...(this.accessToken && { Authorization: `Bearer ${this.accessToken}` }),
      },
    });

    let response = await fetch(url, buildConfig());

    if (response.status === 401 && this.refreshToken) {
      try {
        if (this.isRefreshing) {
          await this.refreshPromise;
        } else {
          await this.tryRefreshToken();
        }
        response = await fetch(url, buildConfig());
      } catch (refreshError) {
        this.handleTokenExpired();
        throw refreshError;
      }
    }

    if (response.status === 401) {
      this.handleTokenExpired();
    }

    return response;
  }

  async get(endpoint) {
    return this.request(endpoint);
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  setAccessToken(token) {
    this.accessToken = token;
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  }

  setRefreshToken(token) {
    this.refreshToken = token;
    if (token) {
      localStorage.setItem('refreshToken', token);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }

  setToken(token) {
    // Compatibilidade com código antigo que usa setToken
    this.setAccessToken(token);
  }

  getToken() {
    return this.accessToken;
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }
}

const api = new ApiService();

// ==================== AUTH SERVICE ====================
export const authService = {
  async login(credentials) {
    const response = await api.post('/auth/login', credentials);
    if (response.success) {
      api.setAccessToken(response.data.accessToken);
      api.setRefreshToken(response.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignora erro se logout falhar (ex: token já expirado)
    } finally {
      api.clearTokens();
    }
  },

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated() {
    return !!this.getUser() && !!api.getToken();
  }
};

// ==================== PLAYERS SERVICE ====================
export const playersService = {
  async getAll(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return await api.get(`/players?${queryString}`);
  },

  async getById(id) {
    return await api.get(`/players/${id}`);
  },

  async create(playerData) {
    return await api.post('/players', playerData);
  },

  async update(id, playerData) {
    return await api.put(`/players/${id}`, playerData);
  },

  async delete(playerId) {
    return await api.delete(`/players/${playerId}`);
  },

  async uploadCsv(file) {
    const token = api.getToken();
    console.log('🔑 Token atual:', token ? '✅ Presente' : '❌ Ausente');

    if (!token) {
      throw new Error('Token de autenticação não encontrado. Faça login novamente.');
    }

    const formData = new FormData();
    formData.append('file', file);
    
    console.log('📤 Fazendo upload para:', `${API_BASE_URL}/players/upload-csv`);

    // authorizedFetch cuida do Authorization + refresh; sem Content-Type (FormData)
    const response = await api.authorizedFetch('/players/upload-csv', {
      method: 'POST',
      body: formData,
    });

    console.log('📥 Status da resposta:', response.status);
    
    const data = await response.json();
    
    if (!response.ok) {
      console.error('❌ Erro na resposta:', data);
      throw {
        status: response.status,
        error: data.error || { message: 'Erro no upload' }
      };
    }

    console.log('✅ Upload realizado com sucesso:', data);
    return data;
  }
};

// ==================== MATCHES SERVICE ====================
export const matchesService = {
  // Buscar partidas
  async getCurrent() {
    console.log('🔍 Buscando partidas em andamento...');
    return await api.get('/matches/current');
  },

  async getReady() {
    console.log('🔍 Buscando partidas prontas...');
    return await api.get('/matches/ready');
  },

  async getHistory(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return await api.get(`/matches/history?${queryString}`);
  },

  async getById(id) {
    return await api.get(`/matches/${id}`);
  },

  async getStats() {
    return await api.get('/matches/stats');
  },

  // Criar e gerenciar partidas
  async create(matchData) {
    return await api.post('/matches', matchData);
  },

  async startMatch(matchId) {
    console.log(`🚀 Iniciando partida ${matchId}...`);
    return await api.post(`/matches/${matchId}/start`);
  },

  async startMultipleMatches(matchIds) {
    console.log(`🚀 Iniciando múltiplas partidas:`, matchIds);
    return await api.post('/matches/start-multiple', { matchIds });
  },

  // Atualizar placar
  async updateScore(matchId, scoreUpdate) {
    console.log(`🎯 Atualizando placar da partida ${matchId}:`, scoreUpdate);
    return await api.patch(`/matches/${matchId}/score`, scoreUpdate);
  },

  async finishMatch(matchId) {
    console.log(`🏁 Finalizando partida ${matchId}...`);
    return await api.post(`/matches/${matchId}/finish`);
  },

  async editResult(matchId, goalsPlayer1, goalsPlayer2) {
    return await api.patch(`/matches/${matchId}/result`, { goalsPlayer1, goalsPlayer2 });
  },

  /**
   * Troca quem joga uma partida que ainda não começou (desempate por pênaltis
   * da repescagem). Envie só o lado que muda.
   */
  async replacePlayers(matchId, { player1Id, player2Id }) {
    const body = {};
    if (player1Id !== undefined) body.player1Id = player1Id;
    if (player2Id !== undefined) body.player2Id = player2Id;
    return await api.patch(`/matches/${matchId}/players`, body);
  },

  // ==================== MÉTODOS DA FILA (SEM MOCKS) ====================
  async getQueue() {
    console.log('🔍 Buscando fila de partidas...');
    return await api.get('/matches/queue');
  },

  async addToQueue(matchId, priority = 0) {
    console.log(`➕ Adicionando partida ${matchId} à fila (prioridade: ${priority})`);
    return await api.post(`/matches/${matchId}/queue`, { priority });
  },

  async removeFromQueue(matchId) {
    console.log(`➖ Removendo partida ${matchId} da fila`);
    return await api.delete(`/matches/${matchId}/queue`);
  },

  async clearQueue() {
    console.log('🗑️ Limpando fila completa');
    return await api.delete('/matches/queue');
  }
};

// ==================== COMPETITION SERVICE ====================
export const competitionService = {
  async create(competitionData) {
    return await api.post('/competition/create', competitionData);
  },

  async getCurrent() {
    return await api.get('/competition/current');
  },

  async drawGroups() {
    return await api.post('/competition/draw-groups');
  },

  async advancePhase(currentPhase) {
    return await api.post('/competition/advance-phase', { currentPhase });
  },

  async getGroups() {
    return await api.get('/competition/groups');
  },

  async swapPlayers(playerId1, playerId2) {
    return await api.post('/competition/swap-players', { playerId1, playerId2 });
  },

  /**
   * DESTRUTIVO: apaga partidas, grupos, classificação e fila.
   * Mantém as inscrições e os atletas — o campeonato recomeça do sorteio.
   */
  async reset() {
    return await api.post('/competition/reset', {});
  },

};

// ==================== RANKINGS SERVICE ====================
export const rankingsService = {
  async getGroups(category) {
    const endpoint = category ? `/rankings/groups?category=${category}` : '/rankings/groups';
    return await api.get(endpoint);
  },

  async getBracket(phase) {
    return await api.get(`/rankings/bracket?phase=${phase}`);
  },

  async getOverall(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return await api.get(`/rankings/overall?${queryString}`);
  },

  async getCompetitionStats() {
    return await api.get('/rankings/stats/competition');
  }
};

// ==================== REGISTRATIONS SERVICE ====================
export const registrationsService = {
  async eligibleCategories(birthDate) {
    return await api.get(`/registrations/eligible-categories?birthDate=${encodeURIComponent(birthDate)}`);
  },

  async create({ name, birthDate, phone, category, paymentProof }) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('birthDate', birthDate);
    formData.append('phone', phone);
    if (category) formData.append('category', category);
    formData.append('paymentProof', paymentProof);

    const response = await fetch(`${API_BASE_URL}/registrations`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) {
      throw { status: response.status, error: data.error || { message: 'Erro na inscrição' } };
    }
    return data;
  },

  async list(status) {
    const query = status ? `?status=${status}` : '';
    return await api.get(`/registrations${query}`);
  },

  async stats() {
    return await api.get('/registrations/stats');
  },

  async approve(id) {
    return await api.patch(`/registrations/${id}/approve`, {});
  },

  async reject(id, reason) {
    return await api.patch(`/registrations/${id}/reject`, { reason });
  },

  async createManual({ name, birthDate, phone, category }) {
    return await api.post('/registrations/manual', { name, birthDate, phone, category });
  },

  async remove(id) {
    return await api.delete(`/registrations/${id}`);
  },

  async getPaymentProofBlobUrl(id) {
    const response = await api.authorizedFetch(`/registrations/${id}/payment-proof`);
    if (!response.ok) {
      throw new Error('Não foi possível carregar comprovante');
    }
    const blob = await response.blob();
    return { url: URL.createObjectURL(blob), mimetype: blob.type };
  },
};

// ==================== BACKUP SERVICE ====================
export const backupService = {
  /**
   * Baixa o snapshot atual do banco (inscrições + atletas) como JSON.
   * Os comprovantes ficam no volume do servidor — nada os apaga — então o
   * arquivo guarda só o caminho deles. Precisa passar pelo authorizedFetch
   * porque uma navegação direta do navegador não envia o header Authorization.
   */
  async download() {
    const response = await api.authorizedFetch('/export/backup');
    if (!response.ok) {
      throw new Error('Não foi possível gerar o backup');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const filename = `backup-goleiro-raiz-${new Date().toISOString().split('T')[0]}.json`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    return filename;
  },

  /**
   * Lê um arquivo .json de backup e devolve o objeto já parseado,
   * para que a tela possa mostrar um resumo antes de confirmar.
   */
  async parseFile(file) {
    const text = await file.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      throw new Error('O arquivo não é um JSON válido.');
    }
    if (!parsed || !Array.isArray(parsed.players) || !Array.isArray(parsed.registrations)) {
      throw new Error('Este arquivo não parece um backup do Goleiro Raiz (faltam "players" e "registrations").');
    }
    return parsed;
  },

  /**
   * DESTRUTIVO: descarta partidas, grupos e classificação e substitui
   * inscrições e atletas pelo conteúdo do backup.
   */
  async restore(snapshot) {
    return await api.post('/export/restore', snapshot);
  },

  /** Apaga andamento, inscrições e atletas para começar um campeonato novo. */
  async startNewChampionship() {
    return await api.post('/export/new-championship', {});
  },
};

// ==================== COMPETITION (extends) ====================
competitionService.simulateGroups = async function () {
  return await api.post('/competition/simulate-groups');
};

competitionService.confirmSimulatedGroups = async function (payload) {
  return await api.post('/competition/confirm-simulated-groups', payload);
};

// ==================== EXPORTAÇÃO PADRÃO ====================
export default api;